import React, { useState, useMemo } from 'react';
import { Crosshair } from 'lucide-react';
import Modal from '../../design-system/Modal';
import Button from '../../design-system/Button';
import { CHAMPION_CRITERIA, buildChampionObjectiveRecord } from '../../lib/championObjectives';

/**
 * APEX v7.7.2 — ChampionObjectiveModal
 *
 * Modale de création d'un objectif lié à un champion.
 * Props :
 *   - champion : nom du champion
 *   - games : pour afficher la stat actuelle
 *   - onClose
 *   - onCreate(record) → miniBlocksDB.add()
 */
export default function ChampionObjectiveModal({ champion, games = [], onClose, onCreate }) {
  const [criterion, setCriterion] = useState('winrate');
  const [targetValue, setTargetValue] = useState(55);
  const [targetGames, setTargetGames] = useState(10);

  const criterionDef = CHAMPION_CRITERIA.find(c => c.value === criterion);

  // Stat actuelle pour le hint contextuel
  const currentStat = useMemo(() => {
    const champGames = games.filter(g =>
      g.champion === champion &&
      !(g.isRemake === true) &&
      !(typeof g.duration === 'number' && g.duration > 0 && g.duration < 5)
    );
    if (champGames.length === 0) return null;

    switch (criterion) {
      case 'winrate': {
        const wins = champGames.filter(g => g.result === 'W').length;
        return { value: Math.round((wins / champGames.length) * 100), unit: '%', n: champGames.length };
      }
      case 'cs_per_min': {
        const withCs = champGames.filter(g => typeof g.csPerMin === 'number');
        if (withCs.length === 0) return null;
        return { value: (withCs.reduce((s, g) => s + g.csPerMin, 0) / withCs.length).toFixed(1), unit: '', n: champGames.length };
      }
      case 'deaths_per_game': {
        return { value: (champGames.reduce((s, g) => s + (g.deaths || 0), 0) / champGames.length).toFixed(1), unit: '', n: champGames.length };
      }
      case 'greed_max': {
        const total = champGames.reduce((s, g) =>
          s + (g.deathDetails || []).filter(d => d?.type === 'greed').length, 0);
        return { value: (total / champGames.length).toFixed(1), unit: '', n: champGames.length };
      }
      default:
        return null;
    }
  }, [champion, games, criterion]);

  const handleCriterionChange = (val) => {
    setCriterion(val);
    const def = CHAMPION_CRITERIA.find(c => c.value === val);
    if (def) setTargetValue(def.defaultTarget);
  };

  const handleCreate = () => {
    const label = `${criterionDef.label} ${criterionDef.direction === 'max' ? '≤' : '≥'} ${targetValue}${criterionDef.unit} sur ${champion}`;
    const description = `${targetGames} games · ${criterionDef.label} cible : ${targetValue}${criterionDef.unit}`;
    const record = buildChampionObjectiveRecord({
      champion,
      criterion,
      target_value: Number(targetValue),
      target_games: Number(targetGames),
      label,
      description
    });
    onCreate(record);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Objectif sur ${champion}`}
      subtitle={<span className="flex items-center gap-1"><Crosshair size={10} /> Champion Objectif</span>}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={handleCreate}>Créer l'objectif</Button>
        </>
      }
    >
        <div className="space-y-4">
          {/* Critère */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
              Type de critère
            </div>
            <div className="grid grid-cols-2 gap-2">
              {CHAMPION_CRITERIA.map(c => (
                <button
                  key={c.value}
                  onClick={() => handleCriterionChange(c.value)}
                  className={`px-3 py-2 text-xs font-bold border transition ${
                    criterion === c.value
                      ? 'border-cyan-400 text-cyan-400 bg-cyan-400/5'
                      : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Valeur cible */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
                Cible {criterionDef.direction === 'max' ? '(max)' : '(min)'}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  step="0.1"
                  className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-sm text-white focus:border-cyan-400 outline-none"
                />
                <span className="text-xs text-zinc-500">{criterionDef.unit || ''}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
                Sur N games
              </div>
              <input
                type="number"
                value={targetGames}
                onChange={(e) => setTargetGames(e.target.value)}
                min="3"
                max="50"
                className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-sm text-white focus:border-cyan-400 outline-none"
              />
            </div>
          </div>

          {/* Hint contextuel */}
          {currentStat && (
            <div className="text-[11px] text-zinc-400 bg-zinc-900/60 border border-zinc-800 px-3 py-2">
              💡 Ton {criterionDef.label} actuel sur {champion} ={' '}
              <span className="text-white font-bold">{currentStat.value}{currentStat.unit}</span>{' '}
              <span className="text-zinc-600">(sur {currentStat.n} games)</span>
            </div>
          )}
        </div>
    </Modal>
  );
}
